import React from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, CheckCircle, FileText, Images, Microscope, Target, BookOpen, Mail, Newspaper, Feather, Building2, Users, HelpCircle, GraduationCap, Shield, Megaphone, Presentation, ShoppingBag, FileBarChart, TrendingUp, BarChart3, Lightbulb, Globe, Calendar } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

interface ContentItemCardProps {
  item: any;
  contentType: 'images' | 'research' | 'pages';
  isSelected: boolean;
  onToggleSelection: (itemId: string) => void;
  index?: number;
  layout?: 'list' | 'grid';
}

// Icon mapping helpers
const getPageTypeIcon = (type: string | null) => {
  const pageTypes = {
    'Standard Document': FileText,
    'Landing Page': FileText,
  };
  return pageTypes[type || 'Standard Document'] || FileText;
};

const getResearchTypeIcon = (type: string | null) => {
  const researchTypes: { [key: string]: React.ComponentType<any> } = {
    'Business & Professional': Building2,
    'Social & Cultural': Users,
    'Education & Training': GraduationCap,
    'Health & Medicine': Shield,
    'Technology & Science': Target,
    'Market Research': TrendingUp,
    'Competitive Analysis': BarChart3,
    'Industry Analysis': Lightbulb,
    'Survey & Statistics': BarChart3,
    'Trend Analysis': TrendingUp,
    'Financial Research': Building2,
    'Academic Research': BookOpen,
    'News & Current Events': Newspaper,
    'Global Research': Globe,
    'Historical Research': Calendar,
    'General Research': Microscope,
  };
  return researchTypes[type || 'General Research'] || Microscope;
};

export const ContentItemCard: React.FC<ContentItemCardProps> = ({
  item,
  contentType,
  isSelected,
  onToggleSelection,
  index = 0,
  layout = 'list'
}) => {
  const displayName = contentType === 'pages' ? item.title : 
                      contentType === 'research' ? item.title : 
                      item.filename;

  const getProjectDisplay = (item: any) => {
    if (item.project) {
      return item.project.title;
    }
    if (item.workbook_id) {
      return 'Unknown Project';
    }
    // Return appropriate label based on content type
    if (contentType === 'pages') return 'Your Documents';
    if (contentType === 'images') return 'Your Images';
    if (contentType === 'research') return 'Your Research';
    return 'Unassigned';
  };

  const projectDisplay = getProjectDisplay(item);

  if (layout === 'grid' && contentType === 'images') {
    // Grid layout for images
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ delay: index * 0.02 }}
        className={cn(
          "relative group cursor-pointer rounded-lg overflow-hidden transition-all duration-200",
          isSelected
            ? "ring-2 ring-blue-500 shadow-lg"
            : "hover:shadow-md"
        )}
        onClick={() => onToggleSelection(item.id)}
      >
        {/* Image */}
        <div className="aspect-square bg-gray-100 dark:bg-gray-800">
          <img 
            src={item.image_url} 
            alt={displayName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200" />
        
        {/* Selection indicator */}
        <div className="absolute top-2 left-2">
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelection(item.id)}
            variant="blue"
            className="bg-white/90 dark:bg-gray-800/90 transition-all duration-200 group-hover:scale-110 group-hover:ring-2 group-hover:ring-blue-400/50 group-hover:ring-offset-1"
          />
        </div>
        

        
        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">
              {displayName}
            </p>
            <p className="text-white/70 text-xs truncate">
              {projectDisplay}
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // List layout for all content types
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ delay: index * 0.02 }}
      className={cn(
        "group flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 cursor-pointer mb-1 w-full overflow-hidden",
        isSelected
          ? "bg-blue-50/70 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600"
          : "bg-gray-50/50 dark:bg-gray-800/30 border-gray-200/50 dark:border-gray-700/50 hover:border-blue-200 dark:hover:border-blue-600"
      )}
      onClick={() => onToggleSelection(item.id)}
    >
      {/* Checkbox */}
      <Checkbox
        checked={isSelected}
        onChange={() => onToggleSelection(item.id)}
        variant="blue"
        className="flex-shrink-0 transition-all duration-200 group-hover:scale-110 group-hover:ring-2 group-hover:ring-blue-400/50 group-hover:ring-offset-1"
      />
      
      {/* Thumbnail or Icon */}
      <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 flex items-center justify-center">
        {contentType === 'pages' ? (
          React.createElement(getPageTypeIcon(item.type), {
            className: "h-6 w-6 text-gray-600 dark:text-gray-300"
          })
        ) : contentType === 'research' ? (
          React.createElement(getResearchTypeIcon(item.research_type), {
            className: "h-6 w-6 text-green-600 dark:text-green-400"
          })
        ) : (
          <img 
            src={item.image_url} 
            alt={displayName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className={cn(
            "text-sm font-medium truncate",
            isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
          )}>
            {displayName}
          </span>
        </div>
        
        <div className="flex items-center gap-2 mt-1">
          <div className="flex items-center gap-1 min-w-0">
            {item.workbook_id ? (
              <FolderOpen size={12} className="text-purple-500" />
            ) : (
              <FolderOpen size={12} className="text-gray-400" />
            )}
            <span className={cn(
              "text-xs truncate",
              item.workbook_id 
                ? "text-purple-600 dark:text-purple-400" 
                : "text-gray-500 dark:text-gray-400"
            )}>
              {projectDisplay}
            </span>
          </div>
        </div>
      </div>
      

    </motion.div>
  );
}; 